import { Permission } from '@/api';
import PermissionWrapper from '@/components/auth/permission-wrapper';
import type { LockableField } from '@/components/table/table-types';
import { FilterStatus } from '@/components/table/toolbar/filter-status';
import LockDropdown from '@/components/table/toolbar/lock-dropdown';
import { VerticalSeparator } from '@/components/table/toolbar/vertical-separator';
import { themeColors } from '@/theme/colors';
import { Button, Drawer } from '@lego/connect-components-react';
import { Flex } from '@lego/klik-ui';
import {
  MRT_GlobalFilterTextField,
  MRT_ShowHideColumnsButton,
  type MRT_TableInstance,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  useMaterialReactTable,
} from 'material-react-table';
import { type RefObject, useRef } from 'react';
import { useSimulationReadOnly } from '../../context/simulation-read-only-context';
import { SimulationView } from '../../simulation-view-constants';
import { getColumnData } from '../../utils/simulation-column-config';
import {
  type RowDataForDisplay,
  SIM_MODAL,
  type SimulationTableToolbarProps,
} from '../../utils/simulation-types';
import { VersionHistoryDrawerContent } from '../version-history-drawer-content';
import ActionsDropdown from './actions-dropdown';
import SimulationViewDropdown from './simulation-view-dropdown';

const SimulationTableToolbar = ({
  table,
  lockedColumn,
  isLel,
  canUploadFile,
  selectedView,
  isPublishDisabled,
  simulationId,
  downloadTriggerRef,
  publishTriggerRef,
  setLockedColumn,
  setPreference,
  setSelectedView,
  setShowModal,
  onDownloadModalOpen,
  columnFilters = [],
  setColumnFilters,
}: SimulationTableToolbarProps) => {
  const filterColumns = Object.entries(getColumnData(undefined, false)).map(
    ([columnKey, column]) => ({
      accessorKey: columnKey,
      header: column.label,
    }),
  );

  const emptyTable = useMaterialReactTable<RowDataForDisplay>({
    columns: filterColumns,
    data: [],
    state: { showGlobalFilter: true, columnFilters },
    onColumnFiltersChange: setColumnFilters,
  });
  const toolbarTable = table ?? emptyTable;

  const {
    isVersionHistoryOpen,
    setIsVersionHistoryOpen,
    setSelectedVersionId,
    isReadOnly,
  } = useSimulationReadOnly();

  const closeVersionHistory = () => {
    setIsVersionHistoryOpen(false);
    setSelectedVersionId(null);
  };

  const userGroupView = isLel ? 'non-core' : 'core';

  const versionHistoryTriggerRef = useRef<HTMLLIElement | null>(null);
  const uploadFileTriggerRef = useRef<HTMLLIElement | null>(null);

  return (
    <Flex
      padding="8px 16px"
      backgroundColor={themeColors.surface.background.light}
      justifyContent="space-between"
      gap="16px"
      alignItems="center"
    >
      <Flex gap="12px" alignItems="center" flex={1}>
        <MRT_GlobalFilterTextField
          table={toolbarTable}
          placeholder="Search"
          disabled={!table}
        />
        <MRT_ToggleFiltersButton table={toolbarTable} disabled={!table} />
        <MRT_ShowHideColumnsButton table={toolbarTable} disabled={!table} />
        <MRT_ToggleFullScreenButton table={toolbarTable} disabled={!table} />
        <VerticalSeparator />
        <SimulationViewDropdown
          selectedView={selectedView}
          setSelectedView={setSelectedView}
          userGroupView={userGroupView}
          setUserGroupView={(view) =>
            setPreference(view === 'non-core' ? 'lel' : 'simple')
          }
        />
        <VerticalSeparator />
        <FilterStatus table={toolbarTable} />
      </Flex>
      <Flex gap="8px" alignItems="center">
        {!isReadOnly && (
          <LockDropdown
            disabled={!table}
            value={lockedColumn}
            onChange={(selected) => {
              if (lockedColumn !== selected) setLockedColumn(selected);
              else setLockedColumn(undefined);
            }}
          />
        )}
        {!table && <ActionsDropdown disabled />}
        {table && selectedView === SimulationView.BASIC && (
          <>
            <Drawer
              triggerRef={downloadTriggerRef}
              isOpen={isVersionHistoryOpen}
              onClose={closeVersionHistory}
              variant="inline"
            >
              <Drawer.Title>Version history</Drawer.Title>
              <Drawer.Content>
                <VersionHistoryDrawerContent simulationId={simulationId} />
              </Drawer.Content>
            </Drawer>
            <ActionsDropdown
              download={{
                onClick: () => {
                  setShowModal(SIM_MODAL.Download);
                  onDownloadModalOpen?.();
                },
                triggerRef: downloadTriggerRef,
              }}
              versionHistory={{
                onClick: () => setIsVersionHistoryOpen(true),
                triggerRef: versionHistoryTriggerRef,
              }}
              uploadFile={
                canUploadFile
                  ? {
                      onClick: () => setShowModal(SIM_MODAL.Upload),
                      triggerRef: uploadFileTriggerRef,
                    }
                  : undefined
              }
            />
          </>
        )}
        <PermissionWrapper requiredPermissions={[Permission.WriteSimulations]}>
          <Button
            aria-label="Publish prices"
            variant="primary"
            appearance="solid"
            size="small"
            disabled={isPublishDisabled || isReadOnly}
            onClick={() => setShowModal(SIM_MODAL.Publish)}
            ref={publishTriggerRef}
          >
            Publish prices
          </Button>
        </PermissionWrapper>
      </Flex>
    </Flex>
  );
};
export default SimulationTableToolbar;
